import type { ButtressDbService } from '@buttress/crag';

import { createLogger } from '../ns-services/logger.js';
import { childWorkflowOf } from './auto-payment-changes.js';

// Opening the job a just-started workflow makes: My Account's automatic payments buttons, and the cards whose buttons start
// a child workflow (#25 phase 6). A new workflow makes its job a moment later, so it is looked for a few times.

const logger = createLogger('workflow-job-open');

export const OPEN_JOB_ATTEMPTS = 5;
export const OPEN_JOB_WAIT_MS = 1000;

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// A job's path in the feed; null without a job
export function jobFeedPath(jobId: string | null | undefined): string | null {
  return jobId ? `/feed/job/${jobId}` : null;
}

// The feed path of the workflow's open job, when this person can read it; null when there is none yet
export async function openJobPathOf(db: ButtressDbService | undefined, workflowId: string): Promise<string | null> {
  if (!db) return null;
  try {
    const found = await db.query('job', { 'workflow.id': { $eq: workflowId }, status: { $eq: 'PENDING' } });
    const [job] = (found?.results ?? []) as unknown as { id?: string }[];
    return jobFeedPath(job?.id);
  } catch (err) {
    logger.warn('Unable to find the workflow job', err);
    return null;
  }
}

/**
 * The feed path of a company's open job of a name that this person can read (the oldest, as the server keeps one open of
 * the kind), or null: My Account's Open link to the Raise the automatic payments operating target card (#25 phase 6,
 * round 5).
 */
export async function openCompanyJobPath(
  db: ButtressDbService | undefined,
  companyId: string | null | undefined,
  name: string,
): Promise<string | null> {
  if (!db || !companyId) return null;
  try {
    const found = await db.query('job', {
      companyId: { $eq: companyId },
      name: { $eq: name },
      status: { $eq: 'PENDING' },
    });
    const jobs = (found?.results ?? []) as unknown as { id?: string; name?: string; status?: string }[];
    return jobFeedPath(jobs.find((job) => job.name === name && job.status === 'PENDING')?.id);
  } catch (err) {
    logger.warn('Unable to find the open job', err);
    return null;
  }
}

/** The feed path of the workflow's open job, looked for a few times while the workflow makes it; null if it never shows. */
export async function waitForOpenJobPath(
  db: ButtressDbService | undefined,
  workflowId: string | null,
  attempts = OPEN_JOB_ATTEMPTS,
  waitMs = OPEN_JOB_WAIT_MS,
): Promise<string | null> {
  for (let attempt = 0; workflowId && attempt < attempts; attempt++) {
    if (attempt > 0) await pause(waitMs);
    const path = await openJobPathOf(db, workflowId);
    if (path) return path;
  }
  return null;
}

// The child a card's workflow names now (the last `CREATED_WORKFLOW_<node>`), or null when it names none or can't be read
export async function currentChildWorkflow(
  db: ButtressDbService | undefined,
  parentWorkflowId: string | null | undefined,
  nodeId: string,
): Promise<string | null> {
  if (!db || !parentWorkflowId) return null;
  try {
    const parent = (await db.getById('workflow', parentWorkflowId)) as { metadata?: unknown } | undefined;
    return childWorkflowOf(parent?.metadata as Parameters<typeof childWorkflowOf>[0], nodeId);
  } catch (err) {
    logger.warn('Unable to read the card workflow', err);
    return null;
  }
}

/**
 * The child workflow a card's `workflow/create` node started, read from the parent's metadata (`CREATED_WORKFLOW_<node>`)
 * a few times while the engine runs the node; null if it never shows. `previous` is the child it named before the press (a
 * reopened card's earlier child, now ended), which is waited past.
 */
export async function waitForChildWorkflow(
  db: ButtressDbService | undefined,
  parentWorkflowId: string | null | undefined,
  nodeId: string,
  previous: string | null = null,
  attempts = OPEN_JOB_ATTEMPTS,
  waitMs = OPEN_JOB_WAIT_MS,
): Promise<string | null> {
  if (!db || !parentWorkflowId) return null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await pause(waitMs);
    const child = await currentChildWorkflow(db, parentWorkflowId, nodeId);
    if (child && child !== previous) return child;
  }
  return null;
}
